/**
 * Display-unit helpers for the Production Gantt page (SI ⇄ US customary).
 *
 * DISPLAY ONLY: every value in state, localStorage and the API stays SI. Each
 * helper's SI branch returns exactly what the page printed before units were
 * added, so the SI screen is unchanged; the US branch converts via lib/units.
 *
 * US units used on the Gantt: raw flow lb/hr, throughput US (short) tons —
 * written "ton" here, as the field units are only 55 px wide — station sizes
 * in inches (nearest ⅛), weights lb, density lb/ft³.
 */
import { QUANTITIES, formatQty, fmtEighths, localiseMessage, type UnitSystem } from "@/lib/units";
import { fmtInt, fmtT } from "./chartUtils";
import { fmtKg } from "./ganttTypes";

const US = (sys: UnitSystem) => sys === "US";

/** Fixed decimals with thousands separators (as lib/units prints US values). */
const fixed = (x: number, d: number) =>
  x.toLocaleString("en-GB", { minimumFractionDigits: d, maximumFractionDigits: d });

// ── Unit labels ──────────────────────────────────────────────────────────────

/** Throughput unit: "t" | "ton" (US short ton). */
export const tUnit = (sys: UnitSystem) => (US(sys) ? "ton" : "t");
/** Raw-flow unit: "kg/h" | "lb/hr". */
export const flowUnit = (sys: UnitSystem) => (US(sys) ? "lb/hr" : "kg/h");
/** Target unit: "t / shift" | "ton/shift". */
export const goalUnit = (sys: UnitSystem) => (US(sys) ? "ton/shift" : "t / shift");
/** Station-size unit: "mm" | "in". */
export const lenUnit = (sys: UnitSystem) => (US(sys) ? "in" : "mm");
/** Weight unit: "kg" | "lb". */
export const massUnit = (sys: UnitSystem) => (US(sys) ? "lb" : "kg");
/** Density unit: "kg/m³" | "lb/ft³". */
export const densUnit = (sys: UnitSystem) => (US(sys) ? "lb/ft³" : "kg/m³");

// ── Chart scale: kg ⇄ the throughput display unit ───────────────────────────

/** kg → chart value: tonnes (SI, exactly kg / 1000 as before) or US tons. */
export const kgToDisp = (kg: number, sys: UnitSystem) =>
  US(sys) ? QUANTITIES.tonnes.toUS(kg / 1000) : kg / 1000;
/** Chart value → kg (inverse of kgToDisp; SI exactly v * 1000 as before). */
export const dispToKg = (v: number, sys: UnitSystem) =>
  US(sys) ? QUANTITIES.tonnes.toSI(v) * 1000 : v * 1000;
/** Tonnes → display value (identity in SI). */
export const tToDisp = (t: number, sys: UnitSystem) => (US(sys) ? QUANTITIES.tonnes.toUS(t) : t);
/** kg/h → flow display value (identity in SI). */
export const flowToDisp = (kgh: number, sys: UnitSystem) => (US(sys) ? QUANTITIES.capacity.toUS(kgh) : kgh);

/** Axis tick suffix: "20t" (SI, as before) or "22 ton". */
export const tickSuffix = (sys: UnitSystem) => (US(sys) ? " ton" : "t");

// ── Formatters ───────────────────────────────────────────────────────────────

/** Throughput from kg, 1 dp: "35.6 t" (SI fmtT) or "39.2 ton". */
export const fmtMassT = (kg: number, sys: UnitSystem) =>
  US(sys) ? `${fixed(kgToDisp(kg, sys), 1)} ton` : fmtT(kg);

/** Throughput number from tonnes, 1 dp, no unit (SI: toFixed(1), as the banner). */
export const tNum1 = (t: number, sys: UnitSystem) =>
  US(sys) ? fixed(tToDisp(t, sys), 1) : t.toFixed(1);

/** A target as entered, without float noise: "20 t". US is a conversion, not
 *  what was typed, so it uses the field's own 1 dp: "22.0 ton" (as the PDF). */
export const goalLabel = (t: number, sys: UnitSystem) =>
  US(sys) ? `${tNum1(t, sys)} ${tUnit(sys)}` : `${+t.toFixed(2)} ${tUnit(sys)}`;

/** A raw "goalT" input string for text: SI unchanged; US converted (1 dp). */
export const goalInputText = (raw: string, sys: UnitSystem) => {
  if (!US(sys)) return raw;
  const x = Number(raw.trim());
  return raw.trim() !== "" && isFinite(x) ? tNum1(x, sys) : raw;
};

/** "35.6 t" / "800 kg" (SI fmtKg) or "39.2 ton" / "1,764 lb". */
export const fmtKgAuto = (kg: number, sys: UnitSystem) => {
  if (!US(sys)) return fmtKg(kg);
  const ton = kgToDisp(kg, sys);
  return ton >= 1 ? `${fixed(ton, 1)} ton` : `${formatQty(kg, "mass", sys, String)} lb`;
};

/** Flow number, no unit: SI via `siFmt` (default fmtInt), US lb/hr (nearest 50 ≥ 1,000). */
export const flowNum = (kgh: number, sys: UnitSystem, siFmt: (v: number) => string = fmtInt) =>
  formatQty(kgh, "capacity", sys, siFmt);
/** Flow with unit: "1,600 kg/h" / "3,550 lb/hr". */
export const fmtFlow = (kgh: number, sys: UnitSystem, siFmt?: (v: number) => string) =>
  `${flowNum(kgh, sys, siFmt)} ${flowUnit(sys)}`;

/** A weight (station / batch), no unit: SI via `siFmt`, US lb. */
export const massNum = (kg: number, sys: UnitSystem, siFmt: (v: number) => string) =>
  formatQty(kg, "mass", sys, siFmt);

/** A stored mm string for text ("810" → "31⅞"); SI and non-numbers unchanged. */
export const lenText = (mm: string, sys: UnitSystem) => {
  if (!US(sys)) return mm;
  const x = Number(mm.trim());
  return mm.trim() !== "" && isFinite(x) ? fmtEighths(QUANTITIES.length.toUS(x)) : mm;
};

// ── Messages (validators, backend optimiser sentences) ──────────────────────

const NUM = String.raw`[−-]?\d+(?:\.\d+)?`;
const RANGE_SEP = String.raw`\s*(?:–| to )\s*`;
const num = (s: string) => Number(s.replace("−", "-"));

/** Replace "<n> tag" and "<a>–<b> tag" with converted numbers + `outUnit`. */
function convertTagged(msg: string, tag: string, conv: (si: number) => string, outUnit: string): string {
  const esc = tag.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");
  return msg
    .replace(new RegExp(`(${NUM})(${RANGE_SEP})(${NUM})\\s*${esc}(?![\\w/³])`, "g"),
      (_m, a, sep, b) => `${conv(num(a))}${sep}${conv(num(b))} ${outUnit}`)
    .replace(new RegExp(`(${NUM})\\s*${esc}(?![\\w/³])`, "g"),
      (_m, a) => `${conv(num(a))} ${outUnit}`);
}

/**
 * Localise a Gantt message for US display: lib/units' localiseMessage (mm,
 * kg/m³, °C, K, kg/h) plus the Gantt's tonne tags — "t / shift", "t/h" (raw
 * flow, e.g. the optimiser's "50 t/h" input limit) and a bare "t". SI and
 * messages without unit-tagged numbers are returned unchanged.
 */
export function localiseGanttText(msg: string, sys: UnitSystem): string;
export function localiseGanttText(msg: string | undefined | null, sys: UnitSystem): string | undefined;
export function localiseGanttText(msg: string | undefined | null, sys: UnitSystem): string | undefined {
  if (msg == null) return undefined;
  if (!US(sys) || !msg) return msg;
  let out = localiseMessage(msg, sys) ?? msg;
  const ton = (t: number) => fixed(tToDisp(t, sys), 1);
  out = convertTagged(out, "t / shift", ton, "ton/shift");
  out = convertTagged(out, "t/h", (t) => formatQty(t * 1000, "capacity", sys, String), "lb/hr");
  out = convertTagged(out, "t", ton, "ton");
  return out;
}
